import { useEffect, useState } from "react";
import { BUILTIN_SHORTCUTS, type BuiltinShortcutId } from "../domain/views";

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export const ACCENT_COLORS = ["blue", "violet", "green", "orange", "red", "pink"] as const;
export type AccentColor = (typeof ACCENT_COLORS)[number];

export interface Settings {
  theme: ThemePreference;
  accent: AccentColor;
  startupShortcut: BuiltinShortcutId;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: "system",
  accent: "orange",
  startupShortcut: "next",
};

export const SETTINGS_STORAGE_KEY = "td.settings";

function oneOf<Option extends string>(options: readonly Option[], candidate: unknown, fallback: Option): Option {
  return options.find((option) => option === candidate) ?? fallback;
}

export function parseSettings(storedText: string | null): Settings {
  let stored: unknown;
  try {
    stored = storedText === null ? undefined : JSON.parse(storedText);
  } catch {
    return DEFAULT_SETTINGS;
  }
  if (typeof stored !== "object" || stored === null) return DEFAULT_SETTINGS;
  const fields: Partial<Record<keyof Settings, unknown>> = stored;
  return {
    theme: oneOf(THEME_PREFERENCES, fields.theme, DEFAULT_SETTINGS.theme),
    accent: oneOf(ACCENT_COLORS, fields.accent, DEFAULT_SETTINGS.accent),
    startupShortcut: oneOf(
      BUILTIN_SHORTCUTS.map((shortcut) => shortcut.id),
      fields.startupShortcut,
      DEFAULT_SETTINGS.startupShortcut,
    ),
  };
}

export function loadSettings(): Settings {
  try {
    return parseSettings(window.localStorage.getItem(SETTINGS_STORAGE_KEY));
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(settings: Settings): void {
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage can be blocked; the choices then last for this session only.
  }
}

export function applySettingsToRoot(settings: Settings): void {
  const root = document.documentElement;
  if (settings.theme === "system") {
    delete root.dataset["theme"];
  } else {
    root.dataset["theme"] = settings.theme;
  }
  root.dataset["accent"] = settings.accent;
}

export function nextThemePreference(current: ThemePreference): ThemePreference {
  const position = THEME_PREFERENCES.indexOf(current);
  return THEME_PREFERENCES[(position + 1) % THEME_PREFERENCES.length] ?? DEFAULT_SETTINGS.theme;
}

export function useSettings(): { settings: Settings; setSettings(update: (current: Settings) => Settings): void } {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  useEffect(() => {
    applySettingsToRoot(settings);
    saveSettings(settings);
  }, [settings]);

  return { settings, setSettings };
}
